import join from 'url-join';

import { asyncAction, createAction, http } from '../util/';
import { showToast } from '../components/toast/actions';
import { TranslatedCopy } from '../intl/TranslatedCopy';

import { requestForecastDepartments } from '../account/actions';

import { accountIdToEnglishName } from '../util/chinaEnglishNameMappings';

const { VITE_API_URL: apiUrl, VITE_API2_URL: api2Url } = import.meta.env;
const api = join(apiUrl, 'v1');
const api2 = join(api2Url, 'v1');

export const setUserAccount = createAction('SET_USER_ACCOUNT');
export const storeUserData = createAction('STORE_USER_DATA');
export const allAccountsLoaded = createAction('ALL_ACCOUNTS_LOADED');
export const setDeepLinkInfoLoading = createAction('SET_DEEP_LINK_INFO_LOADING');
export const setAlwaysShowCart = createAction('SET_ALWAYS_SHOW_CART');

export const getAccountDepartments = asyncAction({
  baseName: 'GET_ACCOUNT_DEPARTMENTS',
  action: action => ({ accountNumber }) => dispatch => {
    dispatch(action.start(accountNumber));

    return http
      .get(join(api, `/account/${accountNumber}/departments`))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error({ error })));
  }
});

export const getBillableAccountDepartments = asyncAction({
  baseName: 'GET_BILLABLE_ACCOUNT_DEPARTMENTS',
  action: action => ({ accountNumber }) => dispatch => {
    dispatch(action.start(accountNumber));

    return http
      .get(join(api, `/account/${accountNumber}/departments`))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error({ error })));
  }
});

export const setLastLogin = asyncAction({
  baseName: 'SET_LAST_LOGIN',
  action: action => userId => dispatch => {
    dispatch(action.start(userId));

    return http
      .patch(join(api2, '/users/current'), { lastLogin: new Date() })
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error({ error })));
  }
});

export const getCurrentUser = asyncAction({
  baseName: 'GET_CURRENT_USER',
  action: action => () => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, '/users/current'))
      .then(({ data: response }) => {
        const userData = {
          ...response,
          accountDepartments: response.accountDepartments.map(dept => ({
            ...dept,
            accountId: `${dept.accountId}`,
            /* Translates non-english account names to English */
            accountName: accountIdToEnglishName[dept.accountId]
              ? `${dept.accountName} - ${accountIdToEnglishName[dept.accountId]}`
              : dept.accountName,
            departments: dept.departments.map(dept => `${dept}`)
          }))
        };
        localStorage.setItem('user', JSON.stringify(userData));

        return dispatch(action.success(userData));
      })
      .catch(error => dispatch(action.error(error)));
  }
});

export const loadUser = () => async dispatch => {
  const { payload: currentUser } = await dispatch(getCurrentUser());

  dispatch(setLastLogin(currentUser.id));

  if (currentUser?.accountDepartments?.length === 1) {
    const accountNumber = currentUser.accountDepartments[0].accountId;

    await dispatch(getAccountDepartments({ accountNumber }));
  }

  dispatch(allAccountsLoaded());
};

export const getAccountDetails = asyncAction({
  baseName: 'GET_ACCOUNT_DETAILS',
  action: action => ({ accountNumber }) => dispatch => {
    dispatch(action.start(accountNumber));
    return http
      .get(join(import.meta.env.VITE_API_URL, `customer/account/v1/${accountNumber}`))
      .then(({ data: response }) => {
        dispatch(requestForecastDepartments({ accountNumber }));
        return dispatch(action.success(response));
      })
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        return dispatch(action.error(error?.message));
      });
  }
});

export const saveCurrentUser = asyncAction({
  baseName: 'SAVE_CURRENT_USER',
  action: action => values => dispatch => {
    dispatch(action.start(values));

    return http
      .patch(join(api2, '/users/current'), values)
      .then(async () => {
        await dispatch(getCurrentUser());

        dispatch(showToast({ type: 'success', message: new TranslatedCopy({ id: 'global.saved' }).toString() }));

        return dispatch(action.success());
      })
      .catch(error => {
        dispatch(
          showToast({
            type: 'error',
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            error
          })
        );

        return dispatch(action.error(error));
      });
  }
});

export const passwordRecover = asyncAction({
  baseName: 'PASSWORD_RECOVER',
  action: action => ({ username: email }) => dispatch => {
    dispatch(action.start(email));

    return http
      .post(join(api2, '/users/forgotPassword'), { email })
      .then(() => dispatch(action.success()))
      .catch(error => dispatch(action.error(error)));
  }
});

export const passwordReset = asyncAction({
  baseName: 'PASSWORD_RESET',
  action: action => values => dispatch => {
    dispatch(action.start(values));

    return http
      .post(join(api2, '/users/resetPassword'), values)
      .then(() => dispatch(action.success()))
      .catch(error => dispatch(action.error(error)));
  }
});
